/** biome-ignore-all lint/a11y/noStaticElementInteractions: we know what we are doing */
/** biome-ignore-all lint/a11y/useAriaPropsSupportedByRole: we know what we are doing */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: we know what we are doing */
import clsx from 'clsx';
import React from 'react';
import { useController } from 'react-hook-form';

import type { Value } from '@gitbook/slate';
import {
    type ButtonProps,
    FieldControllerProvider,
    FieldGroup,
    useFieldProps,
} from '@gitbook/spine-react';

import { useAgentEnabled } from '../Agent';
import { BasicEditor, type BasicEditorProps, type BasicEditorRef } from './BasicEditor';
import {
    InsertAgentMentionButton,
    InsertEmojiButton,
    InsertPageMentionButton,
    InsertUserMentionButton,
} from './buttons';

export interface RichTextareaProps extends Pick<BasicEditorProps, 'typographyClassName'> {
    /**
     * The current value of the input.
     */
    value?: Value;

    /**
     * Callback when the value changes.
     */
    onChange?: (value: Value) => void;

    /**
     * Whether the input can be edited or not
     */
    disabled?: boolean;

    /**
     * The minimum number of rows to display.
     * @default 1
     */
    minRows?: number;

    /**
     * The maximum number of rows to display.
     * @default 5
     */
    maxRows?: number;

    /**
     * The placeholder to display when the input is empty.
     */
    placeholder?: string;

    /**
     * Whether to show the user mentions button.
     */
    withUserMentions?: boolean;

    /**
     * Whether to show the agent mentions button.
     */
    withAgentMentions?: boolean;

    /**
     * Whether to show the emojis button.
     */
    withEmojis?: boolean;

    /**
     * Whether to show the page mentions button.
     */
    withPageMentions?: boolean;

    /**
     * The size of the insert buttons.
     * @default 'medium'
     */
    insertButtonsSize?: ButtonProps['size'];

    /**
     * Submit button to display in the bottom right corner.
     */
    submitButton?: React.ReactNode;

    /**
     * Callback when the user is submitting the value using "Enter+Meta".
     */
    onSubmit?: () => void;

    /**
     * If true, submit requires Meta/Cmd key. If false, Enter submits.
     * @default true
     */
    submitWithMetaKey?: boolean;

    /**
     * Class name to apply to the element.
     */
    className?: string;

    /**
     * Appearance of the textarea.
     * @default "outline"
     */
    kind?: 'blank' | 'outline';
}

export type RichTextareaRef = BasicEditorRef;

/**
 * Basic editor with proper styling like a textarea.
 */
export const RichTextarea = React.forwardRef(function RichTextarea(
    props: RichTextareaProps,
    ref: React.ForwardedRef<RichTextareaRef>
) {
    const {
        placeholder,
        minRows = 1,
        maxRows = 5,
        withUserMentions = false,
        withAgentMentions: withAgentMentionsProp = false,
        withEmojis = false,
        withPageMentions = false,
        insertButtonsSize = 'medium',
        className,
        submitButton,
        onSubmit,
        submitWithMetaKey = true,
        typographyClassName,
        kind = 'outline',
    } = props;

    const withAgentEnabled = useAgentEnabled();
    const withAgentMentions = withAgentMentionsProp && withAgentEnabled;

    const fieldProps = useFieldProps({
        ref,
        value: props.value,
        onChange: props.onChange,
        disabled: props.disabled,
    });
    const value = fieldProps.value as Value | undefined;
    if (!value) {
        throw new Error(
            'Value is required in RichTextarea. It should be passed as a prop or the component should be wrapped in a <RichTextareaField>'
        );
    }

    const onChange: React.Dispatch<React.SetStateAction<Value>> = (input) => {
        fieldProps.onChange?.(typeof input === 'function' ? input(value) : input);
    };

    return (
        <div
            className={clsx(
                'flex w-full cursor-text flex-col rounded-lg bg-base transition-colors',
                {
                    outline: clsx(
                        'border border-base',
                        value.isFocused && !fieldProps.disabled ? 'border-primary ring-2' : null
                    ),
                    blank: '',
                }[kind],
                className
            )}
            style={{
                minHeight: `${minRows * 24}px`,
                maxHeight: maxRows !== Infinity ? `${maxRows * 24}px` : undefined,
            }}
            onClick={() => {
                if (!fieldProps.disabled) {
                    onChange(value.change().focus().value);
                }
            }}
            id={fieldProps.id}
            aria-labelledby={fieldProps['aria-labelledby']}
            aria-describedby={fieldProps['aria-describedby']}
            aria-invalid={fieldProps['aria-invalid']}
            onFocus={() => {
                // Make sure the editor value is set as focused when the field is focused, e.g. by <FocusScope>.
                // Otherwise, the editor value is initially blurred and updateSelection will proceed to blur the DOM node.
                if (!value.isFocused) {
                    onChange(value.change().focus().value);
                    return true;
                }
            }}
        >
            <BasicEditor
                ref={fieldProps.ref}
                readOnly={fieldProps.disabled}
                value={value}
                placeholder={placeholder}
                onChange={(change) => {
                    onChange(change.value);
                }}
                className={clsx(
                    'scrollbar flex flex-1 flex-col overflow-y-auto',
                    {
                        outline: 'px-3 py-2',
                        blank: '',
                    }[kind]
                )}
                onSubmit={onSubmit}
                submitWithMetaKey={submitWithMetaKey}
                onBlur={() => {
                    fieldProps?.onBlur?.();
                }}
                acceptAgentMention={withAgentMentions}
                typographyClassName={typographyClassName}
            />
            {withAgentMentions ||
            withUserMentions ||
            withPageMentions ||
            withEmojis ||
            submitButton ? (
                <div className="flex justify-between px-2 pb-2">
                    <div className="flex">
                        {withAgentMentions ? (
                            <InsertAgentMentionButton
                                value={value}
                                setValue={onChange}
                                size={insertButtonsSize}
                            />
                        ) : null}
                        {withUserMentions ? (
                            <InsertUserMentionButton setValue={onChange} size={insertButtonsSize} />
                        ) : null}
                        {withPageMentions ? (
                            <InsertPageMentionButton setValue={onChange} size={insertButtonsSize} />
                        ) : null}
                        {withEmojis ? (
                            <InsertEmojiButton setValue={onChange} size={insertButtonsSize} />
                        ) : null}
                    </div>
                    {submitButton ? submitButton : null}
                </div>
            ) : null}
        </div>
    );
});

/**
 * Rich text area component used in a form.
 */
export const RichTextareaField = React.forwardRef(function RichTextareaField(
    props: {
        name: string;

        /**
         * If true the value will be required.
         * @default false
         */
        required?: boolean;

        /**
         * Whether the input can be edited or not
         */
        disabled?: boolean;

        /**
         * The children to render.
         */
        children: React.ReactNode;

        /**
         * Class name to apply to the field.
         */
        className?: string;
    },
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const { name, required, disabled, className, children } = props;

    const controller = useController({
        name: name,
        rules: { required: required ? 'Required' : false },
        disabled: disabled,
    });
    return (
        <FieldControllerProvider controller={controller}>
            <FieldGroup className={className} ref={ref}>
                {children}
            </FieldGroup>
        </FieldControllerProvider>
    );
});
